// Fuel register reconciliation — the whole of the arithmetic.
//
// THE MODEL
// =========
// A site is one of three shapes, decided by how many storage tanks the period
// returned. Nothing is configured; the data says which it is.
//
//   DIRECT_ONLY     no bowser or tank. Fuel is bought and issued straight to
//                   machines. Check: register issued for the period == AssetPro
//                   issued.
//   SINGLE_STORAGE  one tank. Direct purchases are written in the SAME register,
//                   showing both the purchase and the issue, so they cancel and
//                   the register's balance is the bowser balance alone.
//                   Check: register closing == AssetPro closing.
//   MULTI_STORAGE   several tanks. Check: each tank's closing, PLUS the total
//                   fuel purchased and issued directly to machines.
//
// If the check passes, the entries behind it are taken to be right and there is
// nothing further to do. If it fails, the reviewer drops into day-wise balances
// (newest first), finds the last day the two sides agreed, and works through the
// entries from there.
//
// CORRECTIONS FLOW FORWARD
// ========================
// Every verdict carries a signed `apDeltaCl` — how much AssetPro's recorded
// quantity would change if the entry were fixed. Those deltas are applied to the
// AssetPro side to produce an ADJUSTED figure. A correction made to an entry five
// days ago therefore reconciles every day after it, and the period as a whole.
// That is the point of the exercise, so it is shown explicitly rather than left
// for the reviewer to work out.
//
// Nothing here writes to AssetPro. An adjusted figure is always labelled as
// adjusted and shown next to the unadjusted one — the discrepancy stays visible
// until it is actually fixed in the source system.
//
// WHERE THE ASSETPRO CLOSING BALANCE COMES FROM
// =============================================
// Every bowser record carries `balance` — AssetPro's own running balance after
// that movement. The closing balance for a day is the balance on the LAST record
// of that day, and for the period it is the balance on the last record in it.
// A day with no movement carries the previous day's balance forward, because no
// movement means no change.
//
// This is deliberately NOT rolled forward as opening + received − issued. On the
// CHABOU tenant (Aug 2026, tank VFO-35) AssetPro reports three different closing
// figures for the same tank and period:
//
//   308  opening 263 + added 11,690 − issued 11,645   (the aggregates)
//   168  the balance on the last record               (the app's day-wise report)
//    48  data.balanceAtEnd                            (the API's own field)
//
// Using the stored balances reproduces the app's own Bowser Day-wise Report
// exactly — 22 of 22 days on that tenant, including the two days where AssetPro's
// own day summary does not satisfy its own arithmetic. Since the reviewer is
// reconciling against what the app shows them, that is the figure to compare
// against. The other two are reported as a disagreement (see `dataIssues`) and
// never quietly discarded.
//
// MISSING IS NOT ZERO. A figure the reviewer has not entered is null, and a
// comparison against it yields null, never a difference of "everything".

import { enumerateDays, fromCl, toCl } from './registerNormalize';

export const SITE_TYPE = {
  DIRECT_ONLY: 'DIRECT_ONLY',
  SINGLE_STORAGE: 'SINGLE_STORAGE',
  MULTI_STORAGE: 'MULTI_STORAGE',
};

export const SITE_TYPE_LABEL = {
  DIRECT_ONLY: 'Direct issues only — no storage',
  SINGLE_STORAGE: 'One storage tank',
  MULTI_STORAGE: 'Multiple storage tanks',
};

// A difference under a hundredth of a litre is the floating point of a hand-written
// register, not a discrepancy.
export const TOLERANCE_CL = 1;

export const VERDICT = {
  OK: 'OK',
  NOT_IN_REGISTER: 'NOT_IN_REGISTER',
  QTY_WRONG: 'QTY_WRONG',
  WRONG_DATE: 'WRONG_DATE',
  WRONG_ASSET: 'WRONG_ASSET',
  WRONG_SOURCE: 'WRONG_SOURCE',
  DUPLICATE: 'DUPLICATE',
  MISSING_IN_ASSETPRO: 'MISSING_IN_ASSETPRO',
};

export const VERDICT_LABEL = {
  OK: 'Agrees with the register',
  NOT_IN_REGISTER: 'Not in the register',
  QTY_WRONG: 'Wrong quantity',
  WRONG_DATE: 'Wrong date',
  WRONG_ASSET: 'Wrong machine — quantity is right',
  WRONG_SOURCE: 'Wrong source — tank vs direct',
  DUPLICATE: 'Posted twice',
  MISSING_IN_ASSETPRO: 'In the register, missing from AssetPro',
};

export const VERDICT_COLOR = {
  OK: 'green',
  NOT_IN_REGISTER: 'red',
  QTY_WRONG: 'orange',
  WRONG_DATE: 'blue',
  WRONG_ASSET: 'gold',
  WRONG_SOURCE: 'purple',
  DUPLICATE: 'red',
  MISSING_IN_ASSETPRO: 'red',
};

// Where a correction lands. Most verdicts move one figure; a wrong source moves
// two at once, because the fuel has to come off one side and go onto the other.
export const EFFECT = {
  TANK_ISSUED: 'TANK_ISSUED',
  TANK_RECEIVED: 'TANK_RECEIVED',
  DIRECT: 'DIRECT',
};

const cl = (v) => toCl(v);

/**
 * Order a tank's movements the way AssetPro does: by time, and by position in the
 * response when several share a timestamp (which happens routinely — a batch of
 * dispenses entered together all carry the same minute). Position is what makes
 * "the last record of the day" a defined thing rather than a coin toss.
 */
const orderRecords = (records) =>
  records
    .slice()
    .sort((a, b) => (a.datetimeMs - b.datetimeMs) || ((a.seq || 0) - (b.seq || 0)));

/**
 * AssetPro's closing balance per day, from its own stored running balances.
 *
 * @returns { byDay, storedByDay, driftDays, lastCl, storedLastCl, recordCount }
 */
/**
 * A tank's closing balance for each day, BUILT FROM ITS MOVEMENTS.
 *
 *   closing(d) = closing(d-1) + received(d) - issued(d)
 *
 * anchored on the period's opening balance. A day with no movement carries the
 * previous balance, because that is what no movement means.
 *
 * WHY IT IS CONSTRUCTED AND NOT READ
 * =================================
 * The API returns no per-day balance. Each record does carry a stored `balance`,
 * and taking the last of each day reproduces AssetPro's own Bowser Day-wise
 * Report — but those stored balances are a cache that has drifted, and they do
 * not satisfy AssetPro's own arithmetic. On tank VFO-35 over Aug 2026 the stored
 * chain jumps on two days (18 and 23 Aug) with no movement to explain it, and
 * `balanceAtEnd` names a third figure again:
 *
 *   308  opening 263 + added 11,690 - issued 11,645   <- what the movements say
 *   168  the stored balance on the last record
 *    48  data.balanceAtEnd
 *
 * The movements are the transactions; the balances are derived from them. When
 * the two disagree the transactions are the better authority, so the
 * reconciliation is built on those and every disagreement is reported rather
 * than resolved silently.
 *
 * NOTE this means a day where AssetPro's stored balance has drifted will NOT
 * match its own Bowser Day-wise Report. That is the point — the drift is the
 * finding.
 *
 * `storedByDay` keeps AssetPro's own figure alongside so the two can be compared.
 */
export const closingBalancesByDay = ({ records = [], days = [], openingCl = null }) => {
  const ordered = orderRecords(records);

  const movementByDay = new Map();
  const storedLastOfDay = new Map();
  ordered.forEach((r) => {
    if (!r.dayKey) return;
    const cur = movementByDay.get(r.dayKey) || { recv: 0, iss: 0 };
    const q = cl(r.quantity);
    if (q !== null) {
      if (r.kind === 'RECEIPT') cur.recv += q;
      else cur.iss += q;
    }
    movementByDay.set(r.dayKey, cur);
    storedLastOfDay.set(r.dayKey, cl(r.balanceAfter));
  });

  const byDay = new Map();
  const storedByDay = new Map();
  const driftDays = [];
  let running = openingCl;

  days.forEach((d) => {
    const mv = movementByDay.get(d);
    if (running !== null && mv) running += mv.recv - mv.iss;
    byDay.set(d, running);

    if (storedLastOfDay.has(d)) {
      const stored = storedLastOfDay.get(d);
      storedByDay.set(d, stored);
      // AssetPro's own balance disagreeing with its own movements is a finding
      // about the source data, not something to reconcile against the register.
      if (stored !== null && running !== null && Math.abs(stored - running) > TOLERANCE_CL) {
        driftDays.push({ date: d, storedCl: stored, builtCl: running });
      }
    }
  });

  return {
    byDay,
    storedByDay,
    driftDays,
    lastCl: running,
    lastDate: days.length ? days[days.length - 1] : null,
    // The stored figure at the end of the period, kept for the cross-check.
    storedLastCl: ordered.length ? cl(ordered[ordered.length - 1].balanceAfter) : openingCl,
    recordCount: ordered.length,
    openingCl,
  };
};

export const detectSiteType = (storages) => {
  if (!storages || storages.length === 0) return SITE_TYPE.DIRECT_ONLY;
  if (storages.length === 1) return SITE_TYPE.SINGLE_STORAGE;
  return SITE_TYPE.MULTI_STORAGE;
};

/**
 * What a reviewed entry should change about AssetPro's recorded figures.
 *
 * Returns a list, because a correction does not always move a single number. A
 * "wrong source" moves two: the fuel comes off the tank's issued total and goes
 * onto the direct total, or the other way round.
 *
 * Sign convention, stated once so no caller has to reason about it again:
 * positive means AssetPro under-recorded that figure and it should go UP.
 *
 * @returns Array<{ type, cl, storageId, date }>
 */
export const effectsOf = (review, entry, periodFrom, periodTo) => {
  if (!review) return [];
  const q = cl(review.quantityL !== null && review.quantityL !== undefined
    ? review.quantityL
    : (entry && entry.quantity));
  if (q === null) return [];

  const kind = (entry && entry.entryKind) || review.kind || 'ISSUE';
  const storageId = (entry && entry.storageId) || review.storageId || null;
  const date = (entry && entry.dayKey) || review.date || null;

  // Where this entry's quantity currently sits.
  const home = kind === 'RECEIPT'
    ? EFFECT.TANK_RECEIVED
    : kind === 'DIRECT' ? EFFECT.DIRECT : EFFECT.TANK_ISSUED;

  const at = (type, amount, sid) => ({ type, cl: amount, storageId: sid === undefined ? storageId : sid, date });

  switch (review.verdict) {
    case VERDICT.NOT_IN_REGISTER:
    case VERDICT.DUPLICATE:
      // The entry should not be there at all.
      return [at(home, -q)];
    case VERDICT.QTY_WRONG: {
      const correct = cl(review.correctQuantityL);
      return correct === null ? [] : [at(home, correct - q)];
    }
    case VERDICT.MISSING_IN_ASSETPRO:
      // The register has a movement AssetPro never recorded.
      return [at(home, q)];
    case VERDICT.WRONG_DATE: {
      // Only a date that moves the entry OUT of the period changes this period's
      // figures. Shuffling it between days inside the period does not.
      const d = review.correctDate;
      if (!d || (d >= periodFrom && d <= periodTo)) return [];
      return [at(home, -q)];
    }
    case VERDICT.WRONG_ASSET:
      // The fuel left the tank either way, and the quantity is right, so no
      // balance moves. It is recorded because per-machine consumption is wrong
      // for two machines until it is re-posted — a finding, not an adjustment.
      // Returning nothing here is what keeps it out of "accounted for" on the
      // day's remaining difference.
      return [];
    case VERDICT.WRONG_SOURCE: {
      // The fuel moved, the source did not. Take it off one side, put it on the
      // other — the total that reached machines is unchanged either way.
      if (kind === 'DIRECT') {
        // Booked as a direct fill, but the register says it came out of a tank.
        const target = review.correctStorageId || null;
        // Without knowing WHICH tank, the effect cannot be placed. It is left out
        // rather than guessed, and the UI requires the tank before saving.
        if (!target) return [];
        return [at(EFFECT.DIRECT, -q, null), at(EFFECT.TANK_ISSUED, q, target)];
      }
      if (kind === 'ISSUE') {
        // Booked as issued from the tank, but it never went through one.
        if (!storageId) return [];
        return [at(EFFECT.TANK_ISSUED, -q, storageId), at(EFFECT.DIRECT, q, null)];
      }
      return [];
    }
    default:
      return [];
  }
};

/**
 * The net effect of a correction on the TANK CLOSING BALANCE — the figure the
 * reviewer is chasing. An issue leaving the tank lowers its closing, so an
 * upward correction to an issue lowers the balance; a receipt is the reverse.
 * A change that only moves the direct total leaves the tank untouched.
 */
export const closingImpactOf = (review, entry, periodFrom, periodTo) =>
  effectsOf(review, entry, periodFrom, periodTo).reduce((acc, e) => {
    if (e.type === EFFECT.TANK_ISSUED) return acc - e.cl;
    if (e.type === EFFECT.TANK_RECEIVED) return acc + e.cl;
    return acc;
  }, 0);

/** Build a lookup of reviews by the entry key they belong to. */
const indexReviews = (reviews) => {
  const byKey = new Map();
  const extra = [];
  (reviews || []).forEach((r) => {
    if (r.entryKey) byKey.set(r.entryKey, r);
    else extra.push(r); // MISSING_IN_ASSETPRO has no AssetPro entry behind it
  });
  return { byKey, extra };
};

/**
 * The reconciliation.
 *
 * @param storages    tanks in scope (filteredStorages from the report)
 * @param apIssues    AssetPro issues out of those tanks
 * @param apReceipts  AssetPro receipts into those tanks
 * @param apDirect    AssetPro market purchases straight into a machine
 * @param declaration what the reviewer typed for the period, keyed by target
 * @param reviews     entry verdicts for this period
 */
export const buildReconciliation = ({
  storages = [],
  apIssues = [],
  apReceipts = [],
  apDirect = [],
  // Every movement of the in-scope tanks, unfiltered by sub-location: a tank's
  // running balance covers everything that went through it, so the chain must
  // not be built from a narrowed set.
  tankRecords = [],
  days = [],
  declaration = {},
  reviews = [],
  periodFrom,
  periodTo,
}) => {
  const siteType = detectSiteType(storages);
  const dataIssues = [];

  // Every correction, resolved to the figures it moves. Built once, because a
  // wrong-source correction lands on a tank that is not the one its own entry
  // belongs to — so effects cannot be gathered by walking each tank's entries.
  const entryByKey = new Map(
    apIssues.concat(apReceipts, apDirect).map((e) => [e.key, e])
  );
  const effects = reviews.reduce((acc, r) => acc.concat(
    effectsOf(r, r.entryKey ? entryByKey.get(r.entryKey) : null, periodFrom, periodTo)
  ), []);
  const sumEffect = (type, storageId) => effects
    .filter((e) => e.type === type)
    .filter((e) => (storageId === undefined ? true : e.storageId === storageId))
    .reduce((acc, e) => acc + e.cl, 0);

  const sum = (rows, get) => rows.reduce((acc, r) => {
    const v = get(r);
    return v === null || v === undefined ? acc : acc + v;
  }, 0);

  const targets = [];

  if (siteType === SITE_TYPE.DIRECT_ONLY) {
    // Everything that reached a machine in the period, however it got there.
    const all = apIssues.concat(apDirect);
    const apCl = sum(all, (r) => cl(r.quantity));
    // No tank here, so everything that reached a machine counts the same way.
    const adjCl = apCl + sumEffect(EFFECT.TANK_ISSUED) + sumEffect(EFFECT.DIRECT);
    const regCl = cl(declaration.issuedLtr);
    targets.push({
      key: 'ISSUED',
      kind: 'ISSUED',
      label: 'Fuel issued to machines',
      hint: 'Total fuel that reached a machine in this period.',
      apCl,
      adjustedApCl: adjCl,
      regCl,
      field: 'issuedLtr',
    });
  } else {
    storages.forEach((s) => {
      const own = tankRecords.filter((r) => r.storageId === s.storageId);
      const chain = closingBalancesByDay({ records: own, days, openingCl: cl(s.opening) });

      // Built from the tank's own movements — the figure the register is
      // reconciled against.
      const apCl = chain.lastCl;

      // AssetPro disagreeing with itself is reported, never resolved silently.
      const declaredCl = cl(s.closing);
      if (declaredCl !== null && apCl !== null && Math.abs(declaredCl - apCl) > TOLERANCE_CL) {
        dataIssues.push({
          key: `balanceAtEnd-${s.storageId}`,
          storageId: s.storageId,
          severity: 'High',
          title: `${s.assetCode}: the reported closing balance does not follow from the movements`,
          detail: `Opening ${fromCl(cl(s.opening)).toFixed(2)} L plus every receipt and issue in the period comes to ${fromCl(apCl).toFixed(2)} L, but the bowser report's balanceAtEnd says ${fromCl(declaredCl).toFixed(2)} L — a gap of ${fromCl(declaredCl - apCl).toFixed(2)} L inside AssetPro's own data. The movements are used here: they are the transactions, and the balance is derived from them.`,
        });
      }

      if (chain.driftDays.length) {
        dataIssues.push({
          key: `drift-${s.storageId}`,
          storageId: s.storageId,
          severity: 'High',
          title: `${s.assetCode}: AssetPro's stored balance drifts from its own movements on ${chain.driftDays.length} day(s)`,
          detail: `${chain.driftDays.slice(0, 5).map((d) => `${d.date} (stored ${fromCl(d.storedCl).toFixed(2)} L against ${fromCl(d.builtCl).toFixed(2)} L from the movements)`).join('; ')}${chain.driftDays.length > 5 ? '; and more' : ''}. Those days will not match the app's own Bowser Day-wise Report, which shows the stored figure. Checking the register against them will not explain the gap — it is inside AssetPro.`,
        });
      }

      // The period totals should equal the sum of the records behind them.
      const aggCl = [cl(s.opening), cl(s.received), cl(s.issued)].every((v) => v !== null)
        ? cl(s.opening) + cl(s.received) - cl(s.issued)
        : null;
      if (aggCl !== null && apCl !== null && Math.abs(aggCl - apCl) > TOLERANCE_CL) {
        dataIssues.push({
          key: `agg-${s.storageId}`,
          storageId: s.storageId,
          severity: 'Medium',
          title: `${s.assetCode}: the period totals do not match the movements behind them`,
          detail: `The report's own received and issued totals give ${fromCl(aggCl).toFixed(2)} L, while summing the individual movements gives ${fromCl(apCl).toFixed(2)} L. Some movements may be missing from the response.`,
        });
      }

      // An issue leaving the tank lowers the closing balance, so an upward
      // correction to an issue lowers the adjusted closing. A receipt is the
      // other way round.
      const issueDelta = sumEffect(EFFECT.TANK_ISSUED, s.storageId);
      const receiptDelta = sumEffect(EFFECT.TANK_RECEIVED, s.storageId);
      const adjustedApCl = apCl === null ? null : apCl - issueDelta + receiptDelta;
      targets.push({
        key: s.storageId,
        kind: 'TANK_CLOSING',
        storageId: s.storageId,
        label: `${s.assetCode} closing balance`,
        subLabel: s.storageName,
        hint: 'Stock left in the tank at the end of the period.',
        apCl,
        declaredClosingCl: declaredCl,
        chain,
        adjustedApCl,
        regCl: cl((declaration.tanks || {})[s.storageId]),
        field: `tanks.${s.storageId}`,
      });
    });

    // Direct fuel is shown at EVERY storage site, not just multi-tank ones, and
    // is scoped by the same Location / Sub Location filter as everything else —
    // otherwise filtering to one location makes the figure vanish exactly when
    // someone wants to reconcile it.
    //
    // Whether it must be entered differs, though. At a multi-tank site it is a
    // required check. At a single-tank site the register writes the direct
    // purchase and its issue together, so they cancel and the bowser balance
    // alone settles the reconciliation — the figure is offered so it CAN be
    // reconciled, but leaving it blank does not hold the period open.
    {
      const apCl = sum(apDirect, (r) => cl(r.quantity));
      const adjCl = apCl + sumEffect(EFFECT.DIRECT);
      const optional = siteType === SITE_TYPE.SINGLE_STORAGE;
      targets.push({
        key: 'DIRECT',
        kind: 'DIRECT',
        label: 'Purchased and issued direct to machines',
        hint: optional
          ? 'Fuel that never passed through the tank. Optional — the bowser balance above already settles the period, but enter it to check this too.'
          : 'Fuel that never passed through a tank.',
        optional,
        apCl,
        adjustedApCl: adjCl,
        regCl: cl(declaration.directLtr),
        field: 'directLtr',
      });
    }
  }

  // Differences, computed only where both sides are actually present.
  targets.forEach((t) => {
    t.diffCl = t.apCl === null || t.regCl === null ? null : t.apCl - t.regCl;
    t.adjustedDiffCl = t.adjustedApCl === null || t.regCl === null ? null : t.adjustedApCl - t.regCl;
    t.hasCorrections = t.adjustedApCl !== t.apCl;
    t.reconciled = t.adjustedDiffCl === null ? null : Math.abs(t.adjustedDiffCl) <= TOLERANCE_CL;
    t.matchedBefore = t.diffCl === null ? null : Math.abs(t.diffCl) <= TOLERANCE_CL;
  });

  const entered = targets.filter((t) => t.regCl !== null).length;
  // An optional figure left blank does not hold the period open. One that HAS
  // been entered is checked like any other.
  const required = targets.filter((t) => !t.optional);
  const enteredRequired = required.filter((t) => t.regCl !== null).length;
  const comparable = targets.filter((t) => t.adjustedDiffCl !== null);
  const outstanding = comparable.filter((t) => !t.reconciled);

  let state;
  if (!entered) state = 'NOT_STARTED';
  else if (enteredRequired < required.length) state = 'INCOMPLETE';
  else if (!outstanding.length) state = 'RECONCILED';
  else state = 'DIFFERS';

  return {
    siteType,
    targets,
    state,
    entered,
    total: targets.length,
    requiredTotal: required.length,
    enteredRequired,
    outstanding,
    // Did corrections do the work? Worth saying out loud.
    reconciledByCorrections:
      state === 'RECONCILED' && targets.some((t) => t.hasCorrections && t.matchedBefore === false),
    totalDiffCl: comparable.length ? comparable.reduce((a, t) => a + t.adjustedDiffCl, 0) : null,
    dataIssues,
  };
};

/**
 * Day-by-day, newest first.
 *
 * DIRECT_ONLY compares the day's issued total; a storage site compares the day's
 * closing balance, rolled forward from AssetPro's period opening because the API
 * returns no per-day balance. Corrections dated on or before a day are applied to
 * that day, which is what makes a fix five days back reconcile everything after it.
 *
 * A day the reviewer has not filled in is MISSING: no difference is computed for
 * it, and it is not evidence of agreement.
 */
export const buildDayWise = ({
  days = [],
  siteType,
  storage = null,
  // Which daily FLOW to break down when this is not a tank balance:
  //   'ISSUED'  every drop that reached a machine, at a site with no tank
  //   'DIRECT'  fuel bought and put straight into a machine, at a site with tanks
  // Left null for a tank, and defaulted for a no-tank site so older callers work.
  flowKind = null,
  apIssues = [],
  apReceipts = [],
  apDirect = [],
  tankRecords = [],
  dailyRows = [],
  reviews = [],
  periodFrom,
  periodTo,
}) => {
  const byDate = new Map(dailyRows.map((r) => [r.date, r]));

  // Same as in buildReconciliation: resolve every correction to the figures it
  // moves, so a wrong-source correction reaches the right tank on the right day.
  const entryByKey = new Map(
    apIssues.concat(apReceipts, apDirect).map((e) => [e.key, e])
  );
  const effects = reviews.reduce((acc, r) => acc.concat(
    effectsOf(r, r.entryKey ? entryByKey.get(r.entryKey) : null, periodFrom, periodTo)
  ), []);
  const effectOn = (type, date, storageId) => effects
    .filter((e) => e.type === type && e.date === date)
    .filter((e) => (storageId === undefined ? true : e.storageId === storageId))
    .reduce((acc, e) => acc + e.cl, 0);

  const sumDay = (entries, date) =>
    entries.filter((e) => e.dayKey === date).reduce((acc, e) => {
      const q = cl(e.quantity);
      return q === null ? acc : acc + q;
    }, 0);

  const rows = [];

  // A DAILY QUANTITY IS NOT A RUNNING BALANCE
  // =========================================
  // Two different things get broken down by day here, and they do not behave
  // the same way.
  //
  //   A tank's CLOSING BALANCE carries. Today's figure contains every day before
  //   it, so once the two sides part company the same gap repeats down the whole
  //   column — which is why the difference is re-anchored on the last day the
  //   reviewer entered (see the storage branch below).
  //
  //   Fuel ISSUED is a flow. Each day stands on its own: the register's total for
  //   that day either matches AssetPro's or it does not, and nothing is carried
  //   into tomorrow. So there is no anchor and no carried difference, and the day
  //   a difference appears IS the day it happened.
  //
  // Both produce the same row shape, so the day-wise screen, the drill into the
  // entries and the printed report treat them alike.
  const measure = storage ? 'BALANCE' : 'FLOW';
  const kind = flowKind || (siteType === SITE_TYPE.DIRECT_ONLY && !storage ? 'ISSUED' : null);

  if (!storage && kind) {
    // At a site with no tank every drop is an issue however it arrived, so both
    // sets count. Where there ARE tanks, this is the direct fuel alone — the tank
    // issues are reconciled by their own closing balance.
    const entries = kind === 'DIRECT' ? apDirect : apIssues.concat(apDirect);
    const types = kind === 'DIRECT' ? [EFFECT.DIRECT] : [EFFECT.TANK_ISSUED, EFFECT.DIRECT];
    const prefix = kind === 'DIRECT' ? 'DIRECT|' : '';
    days.forEach((date) => {
      const apCl = sumDay(entries, date);
      const adjustedApCl = apCl + types.reduce((acc, t) => acc + effectOn(t, date), 0);
      const row = byDate.get(date) || null;
      const regCl = row ? cl(row.valueLtr) : null;
      rows.push({
        key: `${prefix}${date}`,
        date,
        entered: !!row,
        apCl,
        adjustedApCl,
        expectedCl: adjustedApCl,
        regCl,
        // Before corrections, and after.
        diffCl: regCl === null ? null : apCl - regCl,
        adjustedDiffCl: regCl === null ? null : adjustedApCl - regCl,
        // A flow carries nothing forward, so there is no earlier gap to separate
        // out. Kept null so one renderer serves both kinds of day.
        carriedDiffCl: null,
        anchorDate: null,
      });
    });
  } else if (storage) {
    // AssetPro's OWN closing balance per day, from the running balance it stored
    // on each movement. This reproduces the app's Bowser Day-wise Report exactly.
    const own = tankRecords.filter((r) => r.storageId === storage.storageId);
    const chain = closingBalancesByDay({ records: own, days, openingCl: cl(storage.opening) });

    // THE DIFFERENCE IS RE-ANCHORED ON WHAT THE REVIEWER ENTERED
    // ==========================================================
    // Comparing every day against AssetPro's absolute balance is no use for
    // finding where a break started: once the two sides part company on the
    // 20th, every day after it shows the same 60 L and the reviewer cannot tell
    // which one caused it.
    //
    // So each day is measured from the LAST DAY THE REVIEWER ACTUALLY ENTERED,
    // plus the movements since:
    //
    //   expected(d) = registerClosing(anchor) + Σ (received − issued) over (anchor, d]
    //   difference(d) = expected(d) − registerClosing(d)
    //
    // A difference then means something went wrong IN THAT WINDOW, not that a gap
    // is being carried down from earlier. Filling in the 20th makes the 21st to
    // the 23rd fall to zero, which is what points at the 20th.
    //
    // Before any day has been entered there is nothing to anchor on, so AssetPro's
    // own balance is used and the difference is the plain gap. Blank days do not
    // reset the anchor — the window simply spans them.
    let anchorCl = null;
    let anchorDate = null;
    let cumIssueDelta = 0;
    let cumReceiptDelta = 0;
    let prevApCl = cl(storage.opening);

    days.forEach((date) => {
      const dayIssues = apIssues.filter((e) => e.storageId === storage.storageId);
      const dayReceipts = apReceipts.filter((e) => e.storageId === storage.storageId);
      const recvCl = sumDay(dayReceipts, date);
      const issCl = sumDay(dayIssues, date);

      const issDelta = effectOn(EFFECT.TANK_ISSUED, date, storage.storageId);
      const recvDelta = effectOn(EFFECT.TANK_RECEIVED, date, storage.storageId);
      cumIssueDelta += issDelta;
      cumReceiptDelta += recvDelta;

      // The day's net movement, before and after any corrections recorded
      // against it. Keeping both is what lets the entry review show "this much of
      // the day's difference is now accounted for".
      const movementRawCl = recvCl - issCl;
      const movementCl = (recvCl + recvDelta) - (issCl + issDelta);

      const apCl = chain.byDay.has(date) ? chain.byDay.get(date) : null;
      const adjustedApCl = apCl === null ? null : apCl - cumIssueDelta + cumReceiptDelta;

      // The built chain holds by construction, so the useful check is whether
      // AssetPro's OWN stored balance agrees with it on this day. Where it does
      // not, the drift is inside AssetPro and the register cannot explain it.
      const storedCl = chain.storedByDay.has(date) ? chain.storedByDay.get(date) : null;
      const apChainOk = storedCl === null || apCl === null
        ? null
        : Math.abs(storedCl - apCl) <= TOLERANCE_CL;

      const expectedCl = anchorCl !== null ? anchorCl + movementCl : adjustedApCl;
      const expectedRawCl = anchorCl !== null ? anchorCl + movementRawCl : apCl;

      const row = byDate.get(date) || null;
      const regCl = row ? cl(row.valueLtr) : null;

      rows.push({
        key: `${storage.storageId}|${date}`,
        date,
        entered: !!row,
        apOpenCl: prevApCl,
        apRecvCl: recvCl,
        apIssCl: issCl,
        apCl,
        apStoredCl: storedCl,
        adjustedApCl,
        apChainOk,
        anchorDate,
        anchorCl,
        expectedCl,
        movementCl,
        regCl,
        // The headline: what is STILL wrong in this window, after corrections.
        diffCl: expectedRawCl === null || regCl === null ? null : expectedRawCl - regCl,
        adjustedDiffCl: expectedCl === null || regCl === null ? null : expectedCl - regCl,
        // The plain gap against AssetPro, kept so the carried-down amount stays visible.
        carriedDiffCl: adjustedApCl === null || regCl === null ? null : adjustedApCl - regCl,
      });

      if (apCl !== null) prevApCl = apCl;
      // A day the reviewer entered becomes the basis for everything after it.
      // A blank day does not reset it — the window just spans the gap.
      if (regCl !== null) { anchorCl = regCl; anchorDate = date; }
      else if (anchorCl !== null) anchorCl += movementCl;
    });
  }

  rows.forEach((r) => {
    r.matches = r.adjustedDiffCl === null ? null : Math.abs(r.adjustedDiffCl) <= TOLERANCE_CL;
  });

  // Because each day is measured from the last entered day, a non-zero difference
  // means the gap was CREATED on that day — not carried into it. So the days that
  // need looking at are simply the ones that do not match.
  const comparable = rows.filter((r) => r.matches !== null);
  const breakDays = comparable.filter((r) => r.matches === false).map((r) => r.date);
  const breakDate = breakDays.length ? breakDays[breakDays.length - 1] : null;

  return {
    measure,
    flowKind: kind,
    rows: rows.slice().reverse(), // newest first
    chronological: rows,
    breakDate,
    breakDays,
    apChainBreaks: rows.filter((r) => r.apChainOk === false).map((r) => r.date),
    entered: rows.filter((r) => r.entered).length,
    comparable: comparable.length,
    gaps: rows.filter((r) => !r.entered).length,
    allMatch: comparable.length > 0 && comparable.every((r) => r.matches),
  };
};

/**
 * Every AssetPro entry a reviewer needs to look at, in one list.
 *
 * DIRECT FILLS ARE ALWAYS LISTED
 * ==============================
 *   DIRECT_ONLY     there is no tank, so direct fills ARE the issues.
 *   SINGLE_STORAGE  the direct purchase and its issue are written in the SAME
 *                   register as the bowser movements, so the reviewer checks them
 *                   off from the same page.
 *   MULTI_STORAGE   direct fuel is reconciled as its own daily total, and when a
 *                   day does not tally the reviewer has to be able to open that
 *                   day and audit the fills behind it. They were once left out
 *                   here, which left a difference with nothing to check against.
 *
 * The Type filter on the entries screen is what keeps a multi-tank list workable:
 * a reviewer sent here from a direct-fuel day arrives narrowed to Direct.
 *
 * Note that at a site with tanks a correction to a direct fill does NOT move any
 * bowser balance — that fuel never went through a tank. It still belongs on the
 * worklist, because the AssetPro entry is still wrong.
 */
export const buildEntryList = ({ siteType, apIssues = [], apReceipts = [], apDirect = [] }) => {
  const tag = (rows, kind, label) => rows.map((e) => ({ ...e, entryKind: kind, entryKindLabel: label }));
  if (siteType === SITE_TYPE.DIRECT_ONLY) {
    return tag(apIssues, 'ISSUE', 'Issue').concat(tag(apDirect, 'DIRECT', 'Direct'));
  }
  return tag(apIssues, 'ISSUE', 'Issue')
    .concat(tag(apReceipts, 'RECEIPT', 'Receipt into tank'))
    .concat(tag(apDirect, 'DIRECT', 'Direct'));
};

export const summariseReviews = ({ entries = [], reviews = [] }) => {
  const { byKey, extra } = indexReviews(reviews);
  const reviewed = [];
  const unreviewed = [];
  entries.forEach((e) => {
    const r = byKey.get(e.key) || null;
    (r ? reviewed : unreviewed).push({ entry: e, review: r, verdict: r ? r.verdict : null });
  });
  const flagged = reviewed.filter((r) => r.verdict !== VERDICT.OK).length + extra.length;
  return {
    reviewed,
    unreviewed,
    extra,
    counts: {
      total: entries.length,
      reviewed: reviewed.length,
      unreviewed: unreviewed.length,
      ok: reviewed.filter((r) => r.verdict === VERDICT.OK).length,
      flagged,
    },
    progress: entries.length ? reviewed.length / entries.length : 0,
  };
};

/** What has to change in AssetPro, and why. Excludes OK entries. */
export const buildCorrectionWorklist = ({ summary, periodFrom, periodTo }) => {
  const out = [];
  summary.reviewed.forEach(({ entry, review }) => {
    if (!review || review.verdict === VERDICT.OK) return;
    out.push({
      key: review._id,
      date: entry.dayKey,
      voucherNo: entry.voucherNo || null,
      entryId: entry.id || null,
      assetCode: entry.assetCode || null,
      storageName: entry.storageName || null,
      kind: entry.entryKindLabel || 'Issue',
      currentQtyL: entry.quantity,
      verdict: review.verdict,
      verdictLabel: VERDICT_LABEL[review.verdict],
      correctQuantityL: review.correctQuantityL,
      correctDate: review.correctDate,
      note: review.note,
      reviewedBy: review.reviewedBy,
      impactL: fromCl(closingImpactOf(review, entry, periodFrom, periodTo)),
      action: describeAction(review, entry),
    });
  });
  summary.extra.forEach((review) => {
    out.push({
      key: review._id,
      date: review.date,
      assetCode: review.assetCode,
      kind: review.kind === 'RECEIPT' ? 'Receipt into tank' : review.kind === 'DIRECT' ? 'Direct' : 'Issue',
      currentQtyL: null,
      verdict: review.verdict,
      verdictLabel: VERDICT_LABEL[review.verdict],
      correctQuantityL: review.quantityL,
      note: review.note,
      reviewedBy: review.reviewedBy,
      impactL: fromCl(closingImpactOf(review, null, periodFrom, periodTo)),
      action: `Create the missing entry in AssetPro: ${review.quantityL} L${review.assetCode ? ` to ${review.assetCode}` : ''} on ${review.date}.`,
    });
  });
  return out.sort((a, b) => String(a.date).localeCompare(String(b.date)));
};

const describeAction = (r, e) => {
  switch (r.verdict) {
    case VERDICT.NOT_IN_REGISTER:
      return 'Check against the register. If it was never issued, reverse it in AssetPro.';
    case VERDICT.QTY_WRONG:
      return `Change the quantity from ${e.quantity} L to ${r.correctQuantityL} L.`;
    case VERDICT.WRONG_DATE:
      return r.correctDate ? `Move this entry to ${r.correctDate}.` : 'Correct the date in AssetPro.';
    case VERDICT.WRONG_ASSET:
      return r.correctAssetCode
        ? `Re-post these ${e && e.quantity !== undefined ? `${e.quantity} L` : 'litres'} against ${r.correctAssetCode} instead of ${(e && e.assetCode) || 'the machine shown'}. The quantity is right and the tank balance is unaffected — but consumption is overstated for one machine and understated for the other until it is moved.`
        : 'Re-post this against the machine the register names. The quantity is right; only the machine is wrong.';
    case VERDICT.WRONG_SOURCE:
      return (e && e.entryKind) === 'DIRECT'
        ? `Re-post this as an issue from ${r.correctStorageCode || 'the tank'} instead of a direct purchase. The fuel reached the machine either way — it is the tank's stock that is wrong.`
        : 'Re-post this as a direct purchase instead of an issue from the tank. The fuel never went through the tank, so its stock is understated by this amount.';
    case VERDICT.DUPLICATE:
      return 'Remove the duplicate posting in AssetPro.';
    default:
      return '';
  }
};

/**
 * How far a single day has been accounted for.
 *
 * `toFind` is what the day was out by BEFORE any corrections; `accounted` is how
 * much of that the corrections so far have explained; `remaining` is what is
 * still unexplained. When remaining reaches zero the reviewer can stop — the rest
 * of that day's entries do not need checking.
 */
export const dayProgress = (row) => {
  if (!row || row.diffCl === null || row.adjustedDiffCl === null) return null;
  return {
    date: row.date,
    toFindCl: row.diffCl,
    remainingCl: row.adjustedDiffCl,
    accountedCl: row.diffCl - row.adjustedDiffCl,
    reconciled: Math.abs(row.adjustedDiffCl) <= TOLERANCE_CL,
    startedOut: Math.abs(row.diffCl) > TOLERANCE_CL,
  };
};

/**
 * Which bucket a target's day-by-day figures are saved under.
 *
 * A tank keys on its own storage id. The two flows key on their kind, so the
 * no-tank site's 'ISSUED' days keep the key they were written with and direct
 * fuel gets its own — the two are never mixed into one column of numbers.
 */
export const dailyTargetKey = (target) =>
  (target.kind === 'TANK_CLOSING' ? target.storageId : target.kind);

export const periodDays = (from, to) => enumerateDays(from, to);
